import { useState, useEffect } from "react";
import { InputWrapper } from "../../pages/auth/auth.style";
import { getAttachments, createAttachments } from "../../services/user";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { convertToBase64 } from "../../utils";
import FormLoader from "../FormLoader";

export default function AttachmentsSection({ status }) {
  const { admToken, user } = useSelector((state) => state.auth);

  /* -----------------------------
     DOCUMENT CONFIG
  ------------------------------*/

  const singleAttachments = {
    birthCertificate: "Birth Certificate (jpeg)",
    marriageCertificate: "Marriage Certificate",
    fslc: "FSLC",
    phDCertificate: "PhD Certificate",
    nyscCertificate: "NYSC Certificate",
    agreementOnChildProtectionForm:
      "Agreement on Child Protection Form",
    protectionForm: "Recommendation Letter 1",
  };

  const multipleAttachments = {
    oLevelResults: "O' Level Results",
    higherInstitutionCertificates:
      "Higher Institution Certificates",
    pgdOrMastersCertificates:
      "PGD/Masters Certificates",
  };

  /* -----------------------------
     STATE
  ------------------------------*/

  const [existingSingle, setExistingSingle] = useState({});
  const [existingMulti, setExistingMulti] = useState({});

  const [newSingle, setNewSingle] = useState({});
  const [newMulti, setNewMulti] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  /* -----------------------------
     LOAD ATTACHMENTS
  ------------------------------*/

  const loadAttachments = async () => {
    try {
      setLoading(true);

      const res = await getAttachments(
        admToken,
        user?.staffId
      );

      if (res?.status && res.statusCode === 2) {
        const data = res.data;

        setExistingSingle({
          birthCertificate:
            data.birthCertificatePath || "",
          marriageCertificate:
            data.marriageCertificatePath || "",
          fslc: data.fslcPath || "",
          phDCertificate:
            data.phDCertificatePath || "",
          nyscCertificate:
            data.nyscCertificatePath || "",
          agreementOnChildProtectionForm:
            data.agreementOnChildProtectionFormPath || "",
          protectionForm:
            data.protectionFormPath || "",
        });

        setExistingMulti({
          oLevelResults:
            data.oLevelResultsPaths || [],
          higherInstitutionCertificates:
            data.higherInstitutionCertificatesPaths ||
            [],
          pgdOrMastersCertificates:
            data.pgdOrMastersCertificatesPaths || [],
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.staffId) {
      loadAttachments();
    }
  }, [user?.staffId]);

  /* -----------------------------
     FILE HANDLERS
  ------------------------------*/

  const handleSingleUpload = (key, file) => {
    if (!file) return;

    setNewSingle((prev) => ({
      ...prev,
      [key]: file,
    }));
  };

  const handleMultiUpload = (key, files) => {
    const fileArray = Array.from(files);

    setNewMulti((prev) => ({
      ...prev,
      [key]: fileArray,
    }));
  };

  /* -----------------------------
     SAVE
  ------------------------------*/

  const handleSave = async () => {
    setLoadingSave(true);

    try {
      const singleConverted = {};
      const multiConverted = {};

      // Convert single files
      for (const key in newSingle) {
        singleConverted[key] = await convertToBase64(
          newSingle[key]
        );
      }

      // Convert multi files
      for (const key in newMulti) {
        multiConverted[key] = await Promise.all(
          newMulti[key].map((file) =>
            convertToBase64(file)
          )
        );
      }

      const payload = {
        ...singleConverted,
        ...multiConverted,
      };

      const res = await createAttachments(
        admToken,
        payload,
        user?.staffId
      );

      if (res?.status && res.statusCode === 2) {
        toast.success(
          "Attachments saved successfully!"
        );

        setNewSingle({});
        setNewMulti({});
        loadAttachments();
      } else {
        toast.error(
          res.message ||
            "Failed to save attachments"
        );
      }
    } catch (err) {
      toast.error("Unexpected error occurred");
    } finally {
      setLoadingSave(false);
    }
  };

  /* -----------------------------
     UI
  ------------------------------*/

  return (
    <div className="w-full bg-white text-black p-6 leading-7">
      <h2 className="font-bold text-lg mb-4">
        ATTACHMENTS (Jpeg, png only)
      </h2>

      {/* ---------------- SINGLE ---------------- */}

      <ul className="space-y-6">
        {Object.entries(singleAttachments).map(
          ([key, label], index) => (
            <InputWrapper key={key}>
              <li className="flex flex-col">
                <label className="mb-1 font-semibold">
                  ({index + 1}). {label}
                </label>

                <input
                  type="file"
                  accept="image/jpeg, image/png"
                  onChange={(e) =>
                    handleSingleUpload(
                      key,
                      e.target.files[0]
                    )
                  }
                  disabled={
                    !(status === 0 || status === 4)
                  }
                  className="border p-2 border-gray-600 rounded w-full"
                />

                {/* VIEW EXISTING FILE */}
                {existingSingle[key] && (
                  <button
                    onClick={() =>
                      window.open(
                        existingSingle[key],
                        "_blank"
                      )
                    }
                    className="text-blue-600 text-sm mt-2 underline text-left"
                  >
                    View Uploaded File
                  </button>
                )}
              </li>
            </InputWrapper>
          )
        )}
      </ul>

      {/* ---------------- MULTIPLE ---------------- */}

      <h3 className="text-primary font-semibold text-md mt-8 mb-3">
        Attachment with (Multiple entries)
      </h3>

      <ul className="space-y-6">
        {Object.entries(multipleAttachments).map(
          ([key, label], index) => (
            <InputWrapper key={key}>
              <li className="flex flex-col">
                <label className="mb-1 font-semibold">
                  ({index + 1}). {label}
                </label>

                <input
                  type="file"
                  accept="image/jpeg, image/png"
                  multiple
                  onChange={(e) =>
                    handleMultiUpload(
                      key,
                      e.target.files
                    )
                  }
                  disabled={
                    !(status === 0 || status === 4)
                  }
                  className="border p-2 border-gray-600 rounded w-full"
                />

                {/* VIEW EXISTING FILES */}
                {existingMulti[key] &&
                  existingMulti[key].length >
                    0 && (
                    <div className="mt-2 space-y-1">
                      {existingMulti[key].map(
                        (url, idx) => (
                          <button
                            key={idx}
                            onClick={() =>
                              window.open(
                                url,
                                "_blank"
                              )
                            }
                            className="block text-blue-600 text-sm underline text-left"
                          >
                            View Uploaded File{" "}
                            {idx + 1}
                          </button>
                        )
                      )}
                    </div>
                  )}
              </li>
            </InputWrapper>
          )
        )}
      </ul>

      {/* ---------------- SAVE BUTTON ---------------- */}

      {(status === 0 || status === 4) && (
        <div className="flex justify-end my-6">
          <button
            onClick={handleSave}
            disabled={loadingSave}
            className="px-6 py-2 bg-primary text-white rounded-lg disabled:bg-gray-400"
          >
            {loadingSave
              ? "Saving..."
              : "Save Attachments"}
          </button>
        </div>
      )}

      {/* ---------------- TABLE ---------------- */}

      <h3 className="font-semibold text-lg mt-10 mb-3">
        Uploaded Attachments
      </h3>

      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2">
              Document
            </th>
            <th className="border p-2">
              Status
            </th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td
                colSpan="2"
                className="text-center p-4"
              >
                <FormLoader />
              </td>
            </tr>
          ) : (
            <>
              {Object.entries(
                singleAttachments
              ).map(([key, label]) => {
                const uploaded =
                  existingSingle[key] ||
                  newSingle[key];

                return (
                  <tr key={key}>
                    <td className="border p-2">
                      {label}
                    </td>
                    <td className="border p-2 font-semibold">
                      {uploaded ? (
                        <span className="text-green-700">
                          Uploaded
                        </span>
                      ) : (
                        <span className="text-red-500">
                          Not Uploaded
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {Object.entries(
                multipleAttachments
              ).map(([key, label]) => {
                const total =
                  (existingMulti[key]
                    ?.length || 0) +
                  (newMulti[key]
                    ?.length || 0);

                return (
                  <tr key={key}>
                    <td className="border p-2">
                      {label}
                    </td>
                    <td className="border p-2 font-semibold">
                      {total > 0 ? (
                        <span className="text-green-700">
                          {total} File(s)
                        </span>
                      ) : (
                        <span className="text-red-500">
                          Not Uploaded
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
